import React, { createContext, ReactNode, useContext, useState } from 'react';
import IUser from '../interfaces/IUser';

import { useLoginService } from '../services/useLoginService';
import { AppHeader } from '../components/app-header/AppHeader';
import { ICustomization } from '../interfaces/ICustomization';
import { useLocalStorage } from '../services/useLocalStorage';
import IOrderParam from '../interfaces/IOrderParam';
import { useNavigate } from 'react-router-dom';
import AppError from '../errors/AppError';

type PropsUserContext = {
  state: IUser;
  orderParams: IOrderParam | null;
  customization: ICustomization;
  setState: React.Dispatch<React.SetStateAction<IUser>>;
  signIn(username: string, password: string): Promise<void>;
  signOut(): void;
};

const UserContext = createContext<PropsUserContext>({} as PropsUserContext);

const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const localStorage = useLocalStorage();
  const navigate = useNavigate();

  const userLoginService = useLoginService();

  const user = localStorage.getItem('@PEDIDO-WEB:user');
  const customizationStorage = localStorage.getItem('@PEDIDO-WEB:customization');

  const setTheme = () => {
    const customization: ICustomization = localStorage.getItem('@PEDIDO-WEB:customization');

    if (customization) {
      document.documentElement.style.setProperty('--main-color', `${customization.mainColor}` || '#088671');
      document.documentElement.style.setProperty('--hover-color', `${customization.hoverColor}` || '#08816d');
    }
  };

  const {
    id,
    expires_in,
    username,
    access_token,
    refresh_token,
    name,
    email,
    tenantDomain,
    logged,
    typeAuth,
    seller,
    resources,
  } = user || {
    expires_in: 0,
    username: '',
    access_token: '',
    refresh_token: '',
    name: '',
    email: '',
    tenantDomain: '',
    logged: false,
    typeAuth: '',
    seller: null,
    resources: [],
  };

  const [state, setState] = useState<IUser>({
    id,
    expires_in,
    username,
    access_token,
    refresh_token,
    name,
    email,
    tenantDomain,
    logged,
    typeAuth,
    seller,
    resources,
  });

  const { fontColor, hoverColor, mainColor, logoHeader, logoLogin, iFrame, textLogin } = customizationStorage || {
    fontColor: '',
    hoverColor: '',
    mainColor: '',
    logoHeader: '',
    logoLogin: '',
    iFrame: '',
    textLogin: '',
  };

  const [customization, setCustomization] = useState<ICustomization>({
    fontColor,
    hoverColor,
    mainColor,
    logoHeader,
    logoLogin,
    iFrame,
    textLogin,
  });

  const [orderParams, setOrderParams] = useState<IOrderParam | null>(() => {
    return localStorage.getItem('@PEDIDO-WEB:orderParams');
  });

  const signIn = async (username: string, password: string) => {
    const user = await userLoginService.login(username, password);
    setState(user);

    user.customization && setCustomization(user.customization);
    localStorage.setItem('@PEDIDO-WEB:customization', user.customization);

    user.orderParams && setOrderParams(user.orderParams);
    localStorage.setItem('@PEDIDO-WEB:orderParams', user.orderParams);

    delete user.customization;
    delete user.orderParams;

    localStorage.setItem('@PEDIDO-WEB:user', user);

    setTheme();
  };

  function removeJotformAgent() {
    const jotformScript = document.querySelector(
      'script[src="https://cdn.jotfor.ms/s/umd/latest/for-embedded-agent.js"]',
    );
    if (jotformScript) jotformScript.remove();

    const jotformAgentDiv = document.getElementById('JotformAgent-0196a0326ee37810986891dc7c435fc21871');
    if (jotformAgentDiv) jotformAgentDiv.remove();

    window.localStorage.removeItem('jotform-agent-injected');
  }

  const signOut = async () => {
    localStorage.removeItem('@PEDIDO-WEB:user');
    setState({} as IUser);
    removeJotformAgent();
    navigate('/');
  };

  return (
    <UserContext.Provider
      value={{
        state,
        orderParams,
        customization,
        setState,
        signIn,
        signOut,
      }}
    >
      {user && <AppHeader />}
      {children}
    </UserContext.Provider>
  );
};

function useAuth(): PropsUserContext {
  const context = useContext(UserContext);

  if (!context) {
    throw new AppError('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}

export { UserContextProvider, useAuth };
